/**
 * AirplaneDraftService - Gestion locale des brouillons mode avion
 *
 * Ces brouillons sont 100% locaux (pas de sync backend).
 * Les métadonnées sont stockées dans localStorage.
 * Les fichiers audio sont stockés dans IndexedDB via AudioStorageService.
 *
 * Gère également les items mode avion (audio + OCR) pour la nouvelle
 * fonctionnalité de sauvegarde locale en mode avion.
 */

import { createLogger } from './Debug';

const STORAGE_KEY = 'voxia.airplaneDrafts';
const ITEMS_STORAGE_KEY = 'voxia.airplaneItems';
const MAX_AGE_HOURS = 72; // Items expirent après 72h
const log = createLogger('AirplaneDraftService');

class AirplaneDraftService {
  /**
   * Récupère tous les brouillons mode avion
   * @returns {Array<{id: string, reportType: string, createdAt: number, audioCount: number}>}
   */
  getAll() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) return [];

      const drafts = JSON.parse(data);
      // Trier par date décroissante (plus récent en premier)
      return drafts.sort((a, b) => b.createdAt - a.createdAt);
    } catch (error) {
      log.error('Erreur lecture brouillons mode avion:', error);
      return [];
    }
  }

  /**
   * Crée un nouveau brouillon mode avion
   * @param {string} reportType - Type de rapport
   * @returns {string} - ID du brouillon créé
   */
  create(reportType) {
    try {
      const drafts = this.getAll();
      const id = `airplane_${Date.now()}`;

      const newDraft = {
        id,
        reportType: reportType || 'texte',
        createdAt: Date.now(),
        audioCount: 0
      };

      drafts.push(newDraft);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));

      log.info('Brouillon mode avion créé:', id);
      return id;
    } catch (error) {
      log.error('Erreur création brouillon mode avion:', error);
      throw error;
    }
  }

  /**
   * Supprime un brouillon mode avion
   * @param {string} id - ID du brouillon à supprimer
   */
  delete(id) {
    try {
      const drafts = this.getAll();
      const filtered = drafts.filter(d => d.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
      log.info('Brouillon mode avion supprimé:', id);
    } catch (error) {
      log.error('Erreur suppression brouillon mode avion:', error);
    }
  }

  /**
   * Supprime tous les brouillons mode avion
   */
  deleteAll() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      log.info('Tous les brouillons mode avion supprimés');
    } catch (error) {
      log.error('Erreur suppression tous brouillons mode avion:', error);
    }
  }

  /**
   * Met à jour le compteur d'audio d'un brouillon
   * @param {string} id - ID du brouillon
   * @param {number} count - Nouveau compteur
   */
  updateAudioCount(id, count) {
    try {
      const drafts = this.getAll();
      const draft = drafts.find(d => d.id === id);

      if (draft) {
        draft.audioCount = count;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
        log.info(`Brouillon ${id} mis à jour: ${count} audio(s)`);
      }
    } catch (error) {
      log.error('Erreur mise à jour compteur audio:', error);
    }
  }

  /**
   * Récupère un brouillon par son ID
   * @param {string} id - ID du brouillon
   * @returns {Object|null}
   */
  getById(id) {
    const drafts = this.getAll();
    return drafts.find(d => d.id === id) || null;
  }

  /**
   * Vérifie s'il existe des brouillons mode avion
   * @returns {boolean}
   */
  hasAny() {
    return this.getAll().length > 0;
  }

  // ============================================
  // GESTION DES ITEMS MODE AVION (Audio + OCR)
  // ============================================

  /**
   * Récupère tous les items mode avion (audio + OCR)
   * @returns {Array<{id: string, type: 'audio'|'ocr', createdAt: number, ...}>}
   */
  getAllItems() {
    try {
      const data = localStorage.getItem(ITEMS_STORAGE_KEY);
      if (!data) return [];

      const items = JSON.parse(data);
      // Filtrer les items expirés (> 72h)
      const now = Date.now();
      const maxAge = MAX_AGE_HOURS * 60 * 60 * 1000;
      const validItems = items.filter(item => (now - item.createdAt) < maxAge);

      // Sauvegarder si on a filtré des items
      if (validItems.length !== items.length) {
        localStorage.setItem(ITEMS_STORAGE_KEY, JSON.stringify(validItems));
        log.info(`Nettoyage: ${items.length - validItems.length} item(s) expirés supprimés`);
      }

      // Trier par date décroissante (plus récent en premier)
      return validItems.sort((a, b) => b.createdAt - a.createdAt);
    } catch (error) {
      log.error('Erreur lecture items mode avion:', error);
      return [];
    }
  }

  /**
   * Sauvegarde un item audio mode avion
   * @param {Object} params - Paramètres de l'item
   * @param {string} params.fileUri - URI du fichier audio sur le filesystem (Capacitor)
   * @param {number} params.durationSeconds - Durée de l'audio en secondes
   * @param {string} params.reportType - Type de rapport optionnel
   * @returns {string} - ID de l'item créé
   */
  saveAudioItem({ fileUri, durationSeconds = 0, reportType = null }) {
    try {
      const items = this.getAllItems();
      const id = `airplane_audio_${Date.now()}`;

      const newItem = {
        id,
        type: 'audio',
        createdAt: Date.now(),
        fileUri: fileUri || null,        // URI du fichier sur le filesystem
        durationSeconds: durationSeconds || 0,
        reportType: reportType || null
      };

      items.push(newItem);
      localStorage.setItem(ITEMS_STORAGE_KEY, JSON.stringify(items));

      log.info('Item audio mode avion créé:', id, `(${durationSeconds}s)`, fileUri ? `URI: ${fileUri}` : '(no URI)');
      return id;
    } catch (error) {
      log.error('Erreur création item audio mode avion:', error);
      throw error;
    }
  }

  /**
   * Sauvegarde un item texte mode avion (transcript + annexe)
   * @param {Object} params - Paramètres de l'item
   * @param {string} params.transcript - Texte transcrit
   * @param {string} params.annex - Texte de l'annexe
   * @param {string} params.reportType - Type de rapport optionnel
   * @returns {string} - ID de l'item créé
   */
  saveTextItem({ transcript = '', annex = '', reportType = null }) {
    try {
      // Ne pas sauvegarder si les deux sont vides
      if (!transcript?.trim() && !annex?.trim()) {
        return null;
      }

      const items = this.getAllItems();
      const id = `airplane_text_${Date.now()}`;

      const newItem = {
        id,
        type: 'text',
        createdAt: Date.now(),
        transcript: transcript || '',
        annex: annex || '',
        reportType: reportType || null
      };

      items.push(newItem);
      localStorage.setItem(ITEMS_STORAGE_KEY, JSON.stringify(items));

      log.info('Item texte mode avion créé:', id);
      return id;
    } catch (error) {
      log.error('Erreur création item texte mode avion:', error);
      throw error;
    }
  }

  /**
   * Sauvegarde un item OCR mode avion
   * @param {Object} params - Paramètres de l'item
   * @param {string} params.fileUri - URI du fichier sur le filesystem (Capacitor) ou null (web)
   * @param {string} params.fileName - Nom du fichier
   * @param {string} params.fileType - Type MIME du fichier
   * @param {number} params.fileSize - Taille du fichier en bytes
   * @param {string} params.reportType - Type de rapport optionnel
   * @returns {string} - ID de l'item créé
   */
  saveOcrItem({ fileUri = null, fileName, fileType, fileSize, reportType = null }) {
    try {
      const items = this.getAllItems();
      const id = `airplane_ocr_${Date.now()}`;

      const newItem = {
        id,
        type: 'ocr',
        createdAt: Date.now(),
        fileUri: fileUri || null,
        fileName: fileName || 'fichier',
        fileType: fileType || 'application/octet-stream',
        fileSize: fileSize || 0,
        reportType: reportType || null
      };

      items.push(newItem);
      localStorage.setItem(ITEMS_STORAGE_KEY, JSON.stringify(items));

      log.info('Item OCR mode avion créé:', id, fileName, fileUri ? `URI: ${fileUri}` : '(no URI)');
      return id;
    } catch (error) {
      log.error('Erreur création item OCR mode avion:', error);
      throw error;
    }
  }

  /**
   * Récupère un item par son ID
   * @param {string} id - ID de l'item
   * @returns {Object|null}
   */
  getItemById(id) {
    const items = this.getAllItems();
    return items.find(item => item.id === id) || null;
  }

  /**
   * Supprime un item mode avion
   * @param {string} id - ID de l'item à supprimer
   */
  deleteItem(id) {
    try {
      const items = this.getAllItems();
      const filtered = items.filter(item => item.id !== id);
      localStorage.setItem(ITEMS_STORAGE_KEY, JSON.stringify(filtered));
      log.info('Item mode avion supprimé:', id);
    } catch (error) {
      log.error('Erreur suppression item mode avion:', error);
    }
  }

  /**
   * Supprime tous les items mode avion
   */
  deleteAllItems() {
    try {
      localStorage.removeItem(ITEMS_STORAGE_KEY);
      log.info('Tous les items mode avion supprimés');
    } catch (error) {
      log.error('Erreur suppression tous items mode avion:', error);
    }
  }

  /**
   * Compte le nombre d'items mode avion
   * @returns {number}
   */
  getItemsCount() {
    return this.getAllItems().length;
  }

  /**
   * Vérifie s'il existe des items mode avion
   * @returns {boolean}
   */
  hasAnyItems() {
    return this.getItemsCount() > 0;
  }

  /**
   * Récupère les items audio uniquement
   * @returns {Array}
   */
  getAudioItems() {
    return this.getAllItems().filter(item => item.type === 'audio');
  }

  /**
   * Récupère les items OCR uniquement
   * @returns {Array}
   */
  getOcrItems() {
    return this.getAllItems().filter(item => item.type === 'ocr');
  }
}

export const airplaneDraftService = new AirplaneDraftService();
